import React, { useState, useEffect, useRef } from 'react';
import { Mail, ShieldCheck, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import { PasswordResetRequestError, verifyResetOTP, requestPasswordReset } from '../../services/passwordResetService';

interface VerifyResetOTPProps {
  email: string;
  onOtpVerify: (otpCode: string, message: string) => void;
  onBackToRequest: () => void;
  error: string | null;
  setError: (error: string | null) => void;
  message: string | null;
}

const VerifyResetOTP: React.FC<VerifyResetOTPProps> = ({
  email,
  onOtpVerify,
  onBackToRequest,
  error,
  setError,
  message
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedCode.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      
      const lastFilledIndex = Math.min(pastedCode.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5 && newOtp.every(digit => digit)) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedCode = pastedData.replace(/\D/g, '').slice(0, 6).split('');
    
    if (pastedCode.length > 0) {
      const newOtp = [...otp];
      pastedCode.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      
      // Focus the last filled input or the next empty one
      const lastFilledIndex = Math.min(pastedCode.length - 1, 5);
      setTimeout(() => {
        inputRefs.current[lastFilledIndex]?.focus();
      }, 0);
      
      // Auto-submit if all 6 digits are pasted
      if (pastedCode.length === 6) {
        handleSubmit(newOtp.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (otpCode?: string) => {
    const code = otpCode || otp.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await verifyResetOTP(email, code);
      onOtpVerify(code, response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify code';
      setError(errorMessage);
      
      if (errorMessage.includes('expired')) {
        setOtp(['', '', '', '', '', '']);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError(null);

    try {
      await requestPasswordReset(email);
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
      if (err instanceof PasswordResetRequestError && err.retryAfter) setResendCooldown(err.retryAfter);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-[#E8FCFA] rounded-full">
            <ShieldCheck className="h-8 w-8 text-[#07E5D1]" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#1C1C1E] mb-2">Enter Verification Code</h1>
        <p className="text-[#6B7280]">
          We've sent a 6-digit verification code to
        </p>
        <p className="text-sm font-medium text-[#374151] mt-1 flex items-center justify-center">
          <Mail className="h-4 w-4 mr-1" />
          {email}
        </p>
      </div>

      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#374151] mb-3">
              Enter verification code
            </label>
            <div className="flex justify-center space-x-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-lg font-semibold border-2 border-[#E5E7EB] rounded-lg focus:border-[#07E5D1] focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:ring-opacity-50 transition-all"
                  disabled={isLoading}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-[#9CA3AF] text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          <Button
            type="submit"
            fullWidth
            disabled={isLoading || otp.some(digit => !digit)}
            className="bg-[#07E5D1] hover:bg-[#06D4C2] text-white"
          >
            {isLoading ? (
              <>
                <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Verifying Code...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          <div className="text-center space-y-4">
            <div>
              <p className="text-sm text-[#6B7280] mb-2">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading || resendCooldown > 0}
                className="font-medium text-[#07E5D1] hover:text-[#06D4C2] disabled:text-[#9CA3AF] disabled:cursor-not-allowed inline-flex items-center transition-colors"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${resendLoading ? 'animate-spin' : ''}`} />
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : resendLoading
                  ? 'Sending...'
                  : 'Resend code'
                }
              </button>
            </div>

            <button
              type="button"
              onClick={onBackToRequest}
              className="text-sm text-[#6B7280] hover:text-[#374151] transition-colors"
            >
              ← Back to email entry
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default VerifyResetOTP;